import type {
  Agent,
  Category,
  CiRun,
  Profile,
  Repo,
  Task,
  TaskComment,
  TaskEvent,
  TaskPr,
  TaskFollowUp,
  TaskKind,
  TaskQuestion,
  Subscription,
} from '@shared/domain.ts';

/**
 * The client-side cache.
 *
 * Every table is a map keyed by id and every change — whether it came from this
 * browser, another browser, a routine's callback or a GitHub webhook — arrives
 * the same way: as a row, through `applyChange`. There is deliberately no
 * "refetch after mutate" path anywhere in this app. A write goes to the edge
 * function, the row changes, Postgres tells Realtime, Realtime tells everyone,
 * and the reducer below is the only thing that touches state.
 *
 * That is what makes two people watching the same board see the same thing, and
 * what makes a screenshot appear without anybody pressing anything.
 */

export interface Member {
  project_id: string;
  user_id: string;
  role: 'admin' | 'member';
}

export interface Invite {
  id: string;
  project_id: string;
  email: string;
  role: 'admin' | 'member';
  created_at: string;
  accepted_at: string | null;
}

export interface ProjectSettings {
  project_id: string;
  has_github_pat: boolean;
  /** Appended to the end of every brief this project sends. */
  agent_instructions: string | null;
  design_instructions: string | null;
  /** workflow_run names that mean "preview is up" and "this is live". */
  preview_workflow: string | null;
  prod_workflow: string | null;
  /** Check whose success at a pull request's head stands in for approval. */
  merge_waiver_check: string | null;
  /** URL whose answer holds the commit production runs. */
  prod_version_url: string | null;
  /** Status or check whose success marks a commit fit to deploy. */
  deploy_gate_check: string | null;
  /** Boolean input of prod_workflow that ships the tip anyway — the incident switch. */
  deploy_gate_bypass_input: string | null;
}

/** A named way work arrives — see the Intakes screen. */
export interface Intake {
  id: string;
  project_id: string;
  name: string;
  category_id: string | null;
  /** Pinned on every task through this door; null leaves it unclassified. */
  kind: TaskKind | null;
  agent_id: string | null;
  /** Tasks from this door are approved from the outset. */
  auto_merge: boolean;
  /** Work from this door never becomes a task — handed to a routine and forgotten. */
  fire_and_forget: boolean;
  /** Tasks from this door ask auto-pick for a smart routine instead of a normal one. */
  smart: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
  use_count: number;
}

export interface Installation {
  id: number;
  account_login: string;
  html_url: string | null;
  suspended_at: string | null;
}

export interface InstallationRepo {
  installation_id: number;
  owner: string;
  name: string;
  private: boolean;
  default_branch: string | null;
}

export interface DataState {
  loaded: boolean;
  /** Cleared and refilled when the project changes. */
  tasks: Record<string, Task>;
  categories: Record<string, Category>;
  agents: Record<string, Agent>;
  /** The Claude subscriptions routines run on, with their polled usage. */
  subscriptions: Record<string, Subscription>;
  repos: Record<string, Repo>;
  members: Record<string, Member>;
  invites: Record<string, Invite>;
  intakes: Record<string, Intake>;
  profiles: Record<string, Profile>;
  /**
   * Keyed by `project_id`, not `id` — the table's primary key is the project
   * itself, and keeping every held project's row (rather than just the
   * current one) is what lets a task on the "All projects" board answer
   * "does *its* project have a preview workflow?" instead of only the one
   * project a single-project screen happens to have open.
   */
  settings: Record<string, ProjectSettings>;
  installations: Record<string, Installation>;
  installationRepos: InstallationRepo[];
  /** Every CI run we know about, keyed by id. The rollup lives on the task. */
  ciRuns: Record<string, CiRun>;
  /**
   * Every pull request we know about, keyed by id, primary one included — see
   * the `task_prs` migration. Loaded up front like `ciRuns`, not on demand
   * like a thread: there are at most a handful per task, so holding every
   * one for the current project costs nothing and lets the board answer "how
   * many pull requests, and are they all merged" without a fetch per task.
   */
  taskPrs: Record<string, TaskPr>;
  /**
   * Questions routines asked, keyed by id. Read whole, like `taskPrs`: a
   * handful per task at most, and the board needs the open ones for every
   * task to say which are waiting on an answer.
   */
  questions: Record<string, TaskQuestion>;
  /** Follow-ups routines suggested, keyed by id — `suggested` ones head the board. */
  followUps: Record<string, TaskFollowUp>;
  /** Counts for every task; the full thread is only loaded when one is opened. */
  commentCounts: Record<string, number>;
  /** Loaded on demand for the open task. */
  comments: Record<string, TaskComment[]>;
  events: Record<string, TaskEvent[]>;
  /** Set while the realtime socket is not connected, so the UI can say so. */
  live: boolean;
}

export const emptyState: DataState = {
  loaded: false,
  tasks: {},
  categories: {},
  agents: {},
  subscriptions: {},
  repos: {},
  members: {},
  invites: {},
  intakes: {},
  profiles: {},
  settings: {},
  installations: {},
  installationRepos: [],
  ciRuns: {},
  taskPrs: {},
  questions: {},
  followUps: {},
  commentCounts: {},
  comments: {},
  events: {},
  live: false,
};

type Keyed = { id: string };

export type Action =
  | { type: 'reset' }
  | { type: 'loaded'; payload: Partial<DataState> }
  | { type: 'live'; live: boolean }
  | { type: 'upsert'; table: RowTable; row: Record<string, unknown> }
  | { type: 'delete'; table: RowTable; row: Record<string, unknown> }
  | { type: 'thread'; taskId: string; comments: TaskComment[]; events: TaskEvent[] };

export type RowTable =
  | 'tasks'
  | 'categories'
  | 'agents'
  | 'subscriptions'
  | 'repos'
  | 'project_members'
  | 'project_invites'
  | 'intakes'
  | 'profiles'
  | 'project_settings'
  | 'ci_runs'
  | 'task_comments'
  | 'task_events'
  | 'task_prs'
  | 'task_questions'
  | 'task_follow_ups'
  | 'github_installations';

const byId = <T extends Keyed>(rows: T[]): Record<string, T> =>
  Object.fromEntries(rows.map((row) => [row.id, row]));

export const index = byId;

/** Members and invites are keyed by a composite, so they get their own key. */
const memberKey = (row: { project_id: string; user_id: string }) =>
  `${row.project_id}:${row.user_id}`;

function withoutKey<T>(map: Record<string, T>, key: string): Record<string, T> {
  if (!(key in map)) return map;
  const next = { ...map };
  delete next[key];
  return next;
}

export function reducer(state: DataState, action: Action): DataState {
  switch (action.type) {
    case 'reset':
      return { ...emptyState };

    case 'loaded':
      return { ...state, ...action.payload, loaded: true };

    case 'live':
      return state.live === action.live ? state : { ...state, live: action.live };

    case 'thread':
      return {
        ...state,
        comments: {
          ...state.comments,
          [action.taskId]: mergeThread(
            state.comments[action.taskId],
            action.comments,
            oldestCommentFirst,
          ),
        },
        events: {
          ...state.events,
          [action.taskId]: mergeThread(state.events[action.taskId], action.events, oldestEventFirst),
        },
      };

    case 'upsert':
      return upsert(state, action.table, action.row);

    case 'delete':
      return remove(state, action.table, action.row);

    default:
      return state;
  }
}

/**
 * A freshly read thread, merged with what is already held rather than replacing
 * it.
 *
 * The read and the live stream race. `openThread` issues a SELECT; a Realtime
 * insert can land while it is in flight; the SELECT then resolves holding a
 * snapshot taken before that insert. Replacing wholesale drops the newer row —
 * and it stays dropped until the thread happens to be read again, so a comment
 * appears and then vanishes for no reason the person can see.
 *
 * Keyed by id, with the fetched copy winning on content: it is the later read of
 * a row that may have changed since the stream delivered it. Anything the fetch
 * had not yet seen survives. Safe because both tables are append-only from the
 * app's side — a genuine removal arrives as its own delete event.
 */
function mergeThread<T extends { id: string | number }>(
  held: T[] | undefined,
  fetched: T[],
  order: (a: T, b: T) => number,
): T[] {
  if (!held?.length) return fetched;

  const byId = new Map<string | number, T>();
  for (const row of held) byId.set(row.id, row);
  for (const row of fetched) byId.set(row.id, row);

  return [...byId.values()].sort(order);
}

const oldestCommentFirst = (a: TaskComment, b: TaskComment) =>
  a.created_at.localeCompare(b.created_at);
const oldestEventFirst = (a: TaskEvent, b: TaskEvent) => a.id - b.id;

function upsert(state: DataState, table: RowTable, row: Record<string, unknown>): DataState {
  const id = row.id as string;

  switch (table) {
    case 'tasks':
      return { ...state, tasks: { ...state.tasks, [id]: row as unknown as Task } };
    case 'categories':
      return { ...state, categories: { ...state.categories, [id]: row as unknown as Category } };
    case 'agents':
      return { ...state, agents: { ...state.agents, [id]: row as unknown as Agent } };
    case 'subscriptions':
      return { ...state, subscriptions: { ...state.subscriptions, [id]: row as unknown as Subscription } };
    case 'repos':
      return { ...state, repos: { ...state.repos, [id]: row as unknown as Repo } };
    case 'profiles':
      return { ...state, profiles: { ...state.profiles, [id]: row as unknown as Profile } };
    case 'ci_runs':
      return { ...state, ciRuns: { ...state.ciRuns, [id]: row as unknown as CiRun } };
    case 'task_prs':
      return { ...state, taskPrs: { ...state.taskPrs, [id]: row as unknown as TaskPr } };
    case 'intakes':
      return { ...state, intakes: { ...state.intakes, [id]: row as unknown as Intake } };
    case 'task_questions':
      return { ...state, questions: { ...state.questions, [id]: row as unknown as TaskQuestion } };
    case 'task_follow_ups':
      return { ...state, followUps: { ...state.followUps, [id]: row as unknown as TaskFollowUp } };

    case 'project_members': {
      const member = row as unknown as Member;
      return { ...state, members: { ...state.members, [memberKey(member)]: member } };
    }

    case 'project_invites':
      return { ...state, invites: { ...state.invites, [id]: row as unknown as Invite } };

    case 'project_settings': {
      const settings = row as unknown as ProjectSettings;
      return { ...state, settings: { ...state.settings, [settings.project_id]: settings } };
    }

    case 'github_installations':
      return {
        ...state,
        installations: { ...state.installations, [String(id)]: row as unknown as Installation },
      };

    case 'task_comments': {
      const comment = row as unknown as TaskComment;
      const thread = state.comments[comment.task_id];
      // Only bump the count for a comment we have not already counted — an
      // UPDATE (the GitHub URL landing a moment after the insert) must not.
      const known = thread?.some((c) => c.id === comment.id) ?? false;

      // The open thread only holds its latest page, not the whole history —
      // so an unknown row is not necessarily a new comment. It can just as
      // well be an UPDATE to one further back than the page reaches (an old
      // comment's GitHub URL landing late). Tell the two apart by time: a
      // genuine new comment is newer than everything already held. Anything
      // older belongs outside the loaded window — leave the page and the
      // count alone, since that comment was already counted on first load.
      const newestHeld = thread?.length ? thread[thread.length - 1]!.created_at : null;
      if (thread && !known && newestHeld && comment.created_at <= newestHeld) {
        return state;
      }

      const counts = known
        ? state.commentCounts
        : { ...state.commentCounts, [comment.task_id]: (state.commentCounts[comment.task_id] ?? 0) + 1 };

      if (!thread) return { ...state, commentCounts: counts };
      return {
        ...state,
        commentCounts: counts,
        comments: {
          ...state.comments,
          [comment.task_id]: known
            ? thread.map((c) => (c.id === comment.id ? comment : c))
            : [...thread, comment],
        },
      };
    }

    case 'task_events': {
      const event = row as unknown as TaskEvent;
      const thread = state.events[event.task_id];
      // Only kept for a task whose history is open; otherwise it is re-read on
      // open anyway, and holding every event for every task earns nothing.
      if (!thread) return state;
      if (thread.some((e) => e.id === event.id)) return state;
      return { ...state, events: { ...state.events, [event.task_id]: [...thread, event] } };
    }

    default:
      return state;
  }
}

function remove(state: DataState, table: RowTable, row: Record<string, unknown>): DataState {
  const id = row.id as string;

  switch (table) {
    case 'intakes':
      return { ...state, intakes: withoutKey(state.intakes, id) };
    case 'tasks': {
      const { [id]: _gone, ...tasks } = state.tasks;
      return {
        ...state,
        tasks,
        comments: withoutKey(state.comments, id),
        events: withoutKey(state.events, id),
        commentCounts: withoutKey(state.commentCounts, id),
      };
    }
    case 'categories':
      return { ...state, categories: withoutKey(state.categories, id) };
    case 'agents':
      return { ...state, agents: withoutKey(state.agents, id) };
    case 'subscriptions':
      return { ...state, subscriptions: withoutKey(state.subscriptions, id) };
    case 'repos':
      return { ...state, repos: withoutKey(state.repos, id) };
    case 'project_invites':
      return { ...state, invites: withoutKey(state.invites, id) };
    case 'ci_runs':
      return { ...state, ciRuns: withoutKey(state.ciRuns, id) };
    case 'task_prs':
      return { ...state, taskPrs: withoutKey(state.taskPrs, id) };
    case 'task_questions':
      return { ...state, questions: withoutKey(state.questions, id) };
    case 'task_follow_ups':
      return { ...state, followUps: withoutKey(state.followUps, id) };
    case 'project_members':
      return {
        ...state,
        members: withoutKey(state.members, memberKey(row as { project_id: string; user_id: string })),
      };
    case 'task_comments': {
      const comment = row as unknown as TaskComment;
      const thread = state.comments[comment.task_id];
      return {
        ...state,
        commentCounts: {
          ...state.commentCounts,
          [comment.task_id]: Math.max(0, (state.commentCounts[comment.task_id] ?? 1) - 1),
        },
        comments: thread
          ? { ...state.comments, [comment.task_id]: thread.filter((c) => c.id !== comment.id) }
          : state.comments,
      };
    }
    default:
      return state;
  }
}
